import { FEATURE_FLAGS } from '@/constants';
import { useEffect } from 'react';
import { useGlobalStore } from '../global-store';
import type { IAppConfig } from '@/types';

/**
 * Detects device platform and commits an `appConfig` snapshot into the global store.
 *
 * Responsibilities:
 * - Derive the current platform (mobile / tablet / desktop) from the user-agent.
 * - Assemble the `IAppConfig` object from environment variables and feature flags.
 * - Commit the config into the Zustand store via `setAppConfig`.
 */
export const useInitializeAppConfig = (): void => {
  const { setAppConfig } = useGlobalStore();

  useEffect(() => {
    const isMobile = /Mobi|iPhone|Android/i.test(navigator.userAgent);
    const isTablet = !isMobile && /iPad|Android/.test(navigator.userAgent);
    const platform = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

    const appConfig: IAppConfig = {
      apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NEXT_PUBLIC_VITE_BACKEND_URL,
      environment: process.env.NODE_ENV,
      featureFlags: FEATURE_FLAGS,
      userAgent: navigator.userAgent,
      userLanguage: navigator.language,
      userPlatform: platform,
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    setAppConfig(appConfig);
  }, [setAppConfig]);
};
